import { forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx, focusRing } from './cx';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Shows a mono ellipsis and blocks the click without collapsing the
   * button's width. The button keeps the focus while it loads: it is
   * marked aria-disabled, not disabled (see the comment on Button).
   */
  loading?: boolean;
  /** Leading icon from the shared set. Use instead of a glyph. */
  icon?: IconName;
  /** Stretch to the container. Used inside dialogs and narrow mobile columns. */
  block?: boolean;
  children?: ReactNode;
}

/*
 * 44px tall, Instrument Sans 600. Primary is solid green and there is at most one
 * per view. Secondary is a hairline on surface, not a heavy ink box: an ink
 * border reads as loud as the primary and the two then compete. Ghost is
 * green text, because green is the direction's colour for links and
 * actions: it used to be ink text with a hover fill, and a phone has no
 * hover, so "Mark done", "Edit" and "Log a touch" read as labels on every
 * touchscreen. Danger is solid. Each filled variant carries the quiet
 * seated shadow so it sits on the page rather than being printed onto it.
 * Never a gradient.
 *
 * `hover:` only fires where a pointer can hover (tailwind.config.mjs sets
 * hoverOnlyWhenSupported), so a tap no longer leaves a button tinted; the
 * `active:` fill is the feedback a thumb gets instead.
 */
const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-forest text-[rgb(var(--ct-on-brand-rgb))] hover:bg-forest-deep active:bg-forest-deep border border-transparent shadow-card hover:shadow-lift',
  secondary:
    'bg-paper-card text-ink border border-line-strong hover:bg-forest-faint hover:border-forest-soft active:bg-forest-faint shadow-card hover:shadow-lift',
  ghost: 'bg-transparent border border-transparent hover:bg-forest-faint active:bg-forest-faint',
  // `text-white` rather than the on-brand token was the one variant that
  // did not follow the system, and dark is where it showed: the dark red
  // is #ff8d7d, a salmon meant to be read as text on a dark ground, and
  // white on it measures 2.24:1. Every other filled surface in the
  // product, the primary button and every solid Badge, already reads
  // --ct-on-brand, which is white in light (6.98:1 on #ad2118) and the
  // deep ink in dark (8.38:1 on the salmon).
  danger:
    'bg-danger text-[rgb(var(--ct-on-brand-rgb))] hover:brightness-95 active:brightness-95 border border-transparent shadow-card hover:shadow-lift',
};

/*
 * The ghost's green, kept apart from the rest of the variant so that a
 * caller's own text colour replaces it rather than racing it. Twelve
 * destructive ghost buttons say `className="text-danger"` (Remove a member,
 * Cancel batch, Reject evidence, Delete this workspace). Two colour
 * utilities on one element are settled by their order in the stylesheet,
 * not by their order in the class string, and `.text-forest` is generated
 * after `.text-danger`, so every one of them rendered in the action green:
 * a delete that looked like a confirm.
 */
const GHOST_TEXT = 'text-forest hover:text-forest-deep';
const OWN_TEXT_COLOUR = /(?:^|\s)text-(?:danger|warn|success|info|ink|ink-2|ink-3|white|paper|forest|forest-deep|forest-pale)(?=\s|$)/;

const sizes: Record<ButtonSize, string> = {
  // Both clear 44px: sm is visually tighter but keeps the tap target legal.
  sm: 'min-h-tap px-3 py-1.5 text-xs gap-1.5',
  md: 'min-h-tap px-4 py-2 text-sm gap-2',
};

/** One source of the look, shared by the button and the link that wears it. */
export function buttonClass(
  variant: ButtonVariant = 'ghost',
  size: ButtonSize = 'md',
  block?: boolean,
  className?: string,
): string {
  return cx(
    'inline-flex items-center justify-center rounded-ct-sm font-sans font-semibold tracking-[-0.005em]',
    'transition-[background-color,border-color,color,box-shadow] duration-state ease-ct',
    // aria-disabled is a loading button (see Button): it looks the same as
    // a disabled one.
    'disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed',
    focusRing,
    variants[variant],
    variant === 'ghost' && !(className && OWN_TEXT_COLOUR.test(className)) && GHOST_TEXT,
    sizes[size],
    block && 'w-full',
    className,
  );
}

/**
 * The words of a button, as one flex item.
 *
 * Children used to sit directly inside the inline-flex, so a label with a
 * figure in it, `Create batch (<span class="record">2</span> tranches, ...)`,
 * became five flex items and broke onto three lines at a phone width:
 * "Create batch" / "(" / "0 tranches, R 0,00 )". Wrapped in one span, the
 * text flows as a line with the mono figures inside it and wraps like a
 * sentence when it has to. The icon stays outside, so it never wraps away
 * from its word.
 */
function ButtonText({ children }: { children?: ReactNode }) {
  if (children === null || children === undefined || children === false || children === '') return null;
  return <span className="min-w-0 text-left [text-wrap:balance]">{children}</span>;
}

/**
 * While `loading`, the button is aria-disabled rather than disabled.
 *
 * It used to set `disabled`, and a browser takes the focus away from a
 * control the moment it is disabled and leaves it on the page body. So the
 * thumb or the keyboard that pressed "Save profile", or the submit of any
 * sheet, lost its place mid-save: a screen reader was left on the page
 * rather than the button, Tab started again from the top, and Escape no
 * longer reached an open sheet, because the sheet listens for it on its
 * own element and the focus was outside it. A refused save left the sheet
 * open and the keyboard outside it, on the page behind the scrim.
 *
 * Kept enabled, the button keeps the focus through the save and after it.
 * What `disabled` did for a second submission the click handler does now:
 * while loading it cancels the click and stops it there. That covers every
 * way in, because each one is a click on this button: a second tap, Enter
 * or Space on the focused button, and Enter in a field of the form, which
 * the browser turns into a click on the form's first submit button and
 * submits only if that click is not cancelled. A click on a disabled
 * control never reached its ancestors, so it is stopped from bubbling too.
 *
 * A button that cannot be used for some other reason (nothing ticked, a
 * field missing) still gets the real attribute, except while it loads. A
 * save can change what disables it before it ends: Create batch is
 * disabled when no tranche is ticked, and the batch it writes lists its
 * tranches as batched (and so no longer ticked) as soon as the write is
 * made locally, so the button went disabled mid-save and dropped the
 * focus anyway. While loading the click is refused regardless, so the
 * attribute waits until the save is over.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'ghost', size = 'md', loading = false, icon, block, className, disabled, children, type, onClick, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled && !loading}
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      className={buttonClass(variant, size, block, className)}
      {...rest}
      onClick={(e) => {
        if (loading) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick?.(e);
      }}
    >
      {loading ? <Spinner /> : icon ? <Icon name={icon} size={size === 'sm' ? 16 : 18} /> : null}
      <ButtonText>{children}</ButtonText>
    </button>
  );
});

export interface ButtonLinkProps extends Omit<LinkProps, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  block?: boolean;
  className?: string;
}

/**
 * A link that looks like a button. Use it wherever the thing being offered
 * is a destination rather than an action.
 *
 * It exists because the product was written as `<Link><Button/></Link>`,
 * in nine files. That is a button nested inside an anchor, which is
 * invalid HTML (an anchor may not contain interactive content) and it
 * showed up the moment anybody put a keyboard on it: two tab stops for
 * one control, and a screen reader announcing the same words twice under
 * two different roles, "link, Open grants" and then "button, Open grants".
 * A reader has no way to know those are the same thing, or which one does
 * what.
 *
 * One element now, with the role that matches what it does. It is a link,
 * so it opens in a new tab on ctrl-click and can be copied as an address,
 * which a button never could.
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant = 'ghost', size = 'md', icon, block, className, children, ...rest },
  ref,
) {
  return (
    <Link ref={ref} className={buttonClass(variant, size, block, className)} {...rest}>
      {icon ? <Icon name={icon} size={size === 'sm' ? 16 : 18} /> : null}
      <ButtonText>{children as ReactNode}</ButtonText>
    </Link>
  );
});

/**
 * The busy marker. Not a spinning ring: a mono ellipsis, the way a terminal
 * says it is working. Kept under its old name so callers do not change.
 */
export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block shrink-0 font-mono font-semibold leading-none"
      style={{ fontSize: size, width: size, textAlign: 'center' }}
    >
      …
    </span>
  );
}
